import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WORK_TYPES, PRIORITIES, LOCATIONS, PHOTO_CATEGORIES, VEHICLE_TYPES, PJ_ROLES } from "@/lib/constants";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, CheckCircle2, X, ImagePlus, Send } from "lucide-react";

const empty = {
  project_date: new Date().toISOString().slice(0, 10), customer_id: "", vehicle_id: "", work_type: "",
  title: "", description: "", pic_customer: "", pic_phone: "", penanggung_jawab_id: "", penanggung_jawab_name: "",
  plan_in_date: "", target_date: "", priority: "Normal", location: "Area K3", notes: "",
};
const emptyVeh = { brand: "", model: "", year: "", vehicle_type: "", chassis_number: "", engine_number: "" };

function Section({ title, children }) {
  return (<Card className="p-4 space-y-3"><h3 className="font-heading font-semibold text-navy text-sm border-b pb-2">{title}</h3>{children}</Card>);
}

export default function ProjectForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [veh, setVeh] = useState(emptyVeh);
  const [vehFull, setVehFull] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [staff, setStaff] = useState([]);
  const [pending, setPending] = useState([]);
  const [photoCat, setPhotoCat] = useState(PHOTO_CATEGORIES[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const setV = (k, v) => { setVeh((f) => ({ ...f, [k]: v })); setDirty(true); };

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get("/customers", { params: { active: "true" } }),
          api.get("/users").catch(() => ({ data: [] })),
        ]);
        setCustomers(c.data);
        setStaff(s.data.filter((u) => u.active && PJ_ROLES.includes(u.role)));
        if (editing) {
          const { data } = await api.get(`/projects/${id}`);
          setForm({ ...empty, ...data });
          if (data.vehicle) setVeh({ brand: data.vehicle.brand || "", model: data.vehicle.model || "", year: data.vehicle.year || "", vehicle_type: data.vehicle.vehicle_type || "", chassis_number: data.vehicle.chassis_number || "", engine_number: data.vehicle.engine_number || "" });
          setVehFull(data.vehicle || null);
          if (data.customer_id) {
            const v = await api.get("/vehicles", { params: { customer_id: data.customer_id, active: "true" } });
            setVehicles(v.data);
          }
        }
      } finally { setLoading(false); }
    })();
  }, [id, editing]);

  const onCustomer = async (cid) => {
    set("customer_id", cid); set("vehicle_id", "");
    const cust = customers.find((c) => c.id === cid);
    if (cust) { setForm((f) => ({ ...f, pic_customer: f.pic_customer || cust.pic_name || "", pic_phone: f.pic_phone || cust.phone || "" })); setDirty(true); }
    const { data } = await api.get("/vehicles", { params: { customer_id: cid, active: "true" } });
    setVehicles(data);
  };

  const selectedVehicle = vehicles.find((v) => v.id === form.vehicle_id);
  const addPhotos = (files) => setPending((p) => [...p, ...Array.from(files).map((file) => ({ file, category: photoCat, preview: URL.createObjectURL(file) }))]);

  const uploadPending = async (projId) => {
    for (const ph of pending) { const fd = new FormData(); fd.append("file", ph.file); fd.append("category", ph.category); await api.post(`/projects/${projId}/photos`, fd); }
  };

  const save = async (mode) => {
    if (!form.customer_id || !form.vehicle_id || !form.work_type || !form.title) { toast.error("Lengkapi Customer, Kendaraan, Jenis Pekerjaan, dan Judul."); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.penanggung_jawab_id) { const st = staff.find((s) => s.id === form.penanggung_jawab_id); payload.penanggung_jawab_name = st ? st.name : form.penanggung_jawab_name; }
      let proj;
      if (editing) {
        const { data } = await api.put(`/projects/${id}`, payload); proj = data;
        // update vehicle data
        const vid = form.vehicle_id;
        if (vid && vehFull) {
          await api.put(`/vehicles/${vid}`, { ...vehFull, ...veh }).catch((e) => toast.error("Gagal simpan data kendaraan: " + formatApiErrorDetail(e.response?.data?.detail)));
        }
        if (pending.length) await uploadPending(id);
      } else {
        const stage = mode === "submit" ? "Menunggu Persetujuan" : "Draft";
        const { data } = await api.post("/projects", { ...payload, stage }); proj = data;
        if (pending.length) await uploadPending(proj.id);
      }
      toast.success(`Kartu Proyek ${proj.project_id} disimpan.`);
      setDirty(false); navigate(`/projects/${proj.id}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const goBack = () => { if (dirty && !window.confirm("Perubahan belum disimpan. Yakin ingin keluar?")) return; navigate(-1); };
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4 max-w-4xl pb-20" data-testid="project-form-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={goBack} data-testid="back-button"><ArrowLeft size={18} /></Button>
        <div>
          <h1 className="font-heading text-xl font-bold text-navy">{editing ? "Edit Kartu Proyek" : "Buat Proyek Baru"}</h1>
          {editing && <span className="font-mono-k3 text-xs text-primary font-semibold">{form.project_id}</span>}
        </div>
      </div>

      <Section title="1. Customer & Kendaraan">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1"><Label>Tanggal Proyek Dibuat</Label>
            <Input type="date" value={form.project_date} onChange={(e) => set("project_date", e.target.value)} data-testid="project-date" /></div>
          <div className="space-y-1"><Label>Customer *</Label>
            <Select value={form.customer_id} onValueChange={onCustomer} disabled={editing}>
              <SelectTrigger data-testid="select-customer"><SelectValue placeholder="Pilih customer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name} ({c.customer_id})</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1"><Label>Kendaraan *</Label>
            <Select value={form.vehicle_id} onValueChange={(v) => set("vehicle_id", v)} disabled={editing || !form.customer_id}>
              <SelectTrigger data-testid="select-vehicle"><SelectValue placeholder={form.customer_id ? "Pilih kendaraan" : "Pilih customer dahulu"} /></SelectTrigger>
              <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate} · {v.brand} {v.model}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        {!editing && selectedVehicle && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs bg-slate-50 p-3 rounded-md">
            <div><span className="text-muted-foreground">No. Polisi</span><div className="font-semibold">{selectedVehicle.plate}</div></div>
            <div><span className="text-muted-foreground">Merek</span><div className="font-semibold">{selectedVehicle.brand || "-"}</div></div>
            <div><span className="text-muted-foreground">Model</span><div className="font-semibold">{selectedVehicle.model || "-"}</div></div>
            <div><span className="text-muted-foreground">Tahun</span><div className="font-semibold">{selectedVehicle.year || "-"}</div></div>
          </div>
        )}
      </Section>

      {editing && (
        <Section title="2. Data Kendaraan (lengkapi)">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Merek</Label><Input value={veh.brand} onChange={(e) => setV("brand", e.target.value)} data-testid="veh-brand" placeholder="cth: FAW" /></div>
            <div className="space-y-1"><Label>Tipe / Model</Label><Input value={veh.model} onChange={(e) => setV("model", e.target.value)} data-testid="veh-model" /></div>
            <div className="space-y-1"><Label>Tahun</Label><Input value={veh.year} onChange={(e) => setV("year", e.target.value)} data-testid="veh-year" placeholder="cth: 2021" /></div>
            <div className="space-y-1"><Label>Jenis Kendaraan</Label>
              <Select value={veh.vehicle_type || undefined} onValueChange={(v) => setV("vehicle_type", v)}>
                <SelectTrigger data-testid="veh-type"><SelectValue placeholder="Pilih jenis" /></SelectTrigger>
                <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label>Nomor Rangka</Label><Input value={veh.chassis_number} onChange={(e) => setV("chassis_number", e.target.value)} data-testid="veh-chassis" /></div>
            <div className="space-y-1"><Label>Nomor Mesin</Label><Input value={veh.engine_number} onChange={(e) => setV("engine_number", e.target.value)} data-testid="veh-engine" /></div>
          </div>
        </Section>
      )}

      <Section title="3. Detail Pekerjaan">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1"><Label>Jenis Pekerjaan *</Label>
            <Select value={form.work_type} onValueChange={(v) => set("work_type", v)}>
              <SelectTrigger data-testid="select-worktype"><SelectValue placeholder="Pilih jenis" /></SelectTrigger>
              <SelectContent>{WORK_TYPES.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1"><Label>Judul Proyek *</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="project-title" placeholder="cth: Repair kabin & pengecatan" /></div>
        </div>
        <div className="space-y-1"><Label>Deskripsi Pekerjaan</Label>
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="project-desc" rows={2} /></div>
      </Section>

      <Section title="4. Penanggung Jawab & Kontak">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1"><Label>PIC Customer</Label><Input value={form.pic_customer} onChange={(e) => set("pic_customer", e.target.value)} data-testid="pic-customer" /></div>
          <div className="space-y-1"><Label>No. Telepon PIC</Label><Input value={form.pic_phone} onChange={(e) => set("pic_phone", e.target.value)} data-testid="pic-phone" /></div>
          <div className="space-y-1 sm:col-span-2"><Label>Penanggung Jawab Proyek</Label>
            <Select value={form.penanggung_jawab_id || "none"} onValueChange={(v) => set("penanggung_jawab_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="select-pj"><SelectValue placeholder="Pilih penanggung jawab" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Belum ditentukan</SelectItem>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.role})</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
      </Section>

      <Section title="5. Jadwal, Prioritas & Catatan">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1"><Label>Tanggal Masuk (rencana)</Label><Input type="date" value={form.plan_in_date || ""} onChange={(e) => set("plan_in_date", e.target.value)} data-testid="plan-in-date" /></div>
          <div className="space-y-1"><Label>Target Selesai</Label><Input type="date" value={form.target_date || ""} onChange={(e) => set("target_date", e.target.value)} data-testid="target-date" /></div>
          <div className="space-y-1"><Label>Prioritas</Label>
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}><SelectTrigger data-testid="select-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((pp) => <SelectItem key={pp} value={pp}>{pp}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1"><Label>Lokasi Unit</Label>
            <Select value={form.location} onValueChange={(v) => set("location", v)}><SelectTrigger data-testid="select-location"><SelectValue /></SelectTrigger>
              <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <div className="space-y-1"><Label>Catatan Khusus Customer</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="project-notes" rows={2} /></div>
      </Section>

      <Section title="6. Foto Kondisi Awal (bisa lebih dari satu)">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={photoCat} onValueChange={setPhotoCat}>
            <SelectTrigger className="w-[180px]" data-testid="photo-category"><SelectValue /></SelectTrigger>
            <SelectContent>{PHOTO_CATEGORIES.map((pc) => <SelectItem key={pc} value={pc}>{pc}</SelectItem>)}</SelectContent>
          </Select>
          <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-md text-sm cursor-pointer hover:bg-slate-50" data-testid="photo-add-label">
            <ImagePlus size={16} /> Tambah Foto
            <input type="file" accept="image/*" capture="environment" multiple className="hidden" data-testid="photo-input" onChange={(e) => addPhotos(e.target.files)} />
          </label>
        </div>
        {pending.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {pending.map((ph, i) => (
              <div key={i} className="relative group">
                <img src={ph.preview} alt="" className="w-full h-24 object-cover rounded-md border" />
                <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate">{ph.category}</span>
                <button type="button" onClick={() => setPending((p) => p.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 bg-primary text-white rounded-full p-0.5" data-testid={`remove-photo-${i}`}><X size={12} /></button>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">Foto akan diunggah saat proyek disimpan.</p>
      </Section>

      <div className="flex flex-col sm:flex-row gap-2 sticky bottom-0 bg-background py-3 border-t">
        {editing ? (
          <Button onClick={() => save("edit")} disabled={saving} data-testid="save-changes-btn" className="bg-primary hover:bg-red-700 h-11">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save size={16} className="mr-1.5" />} Simpan Perubahan
          </Button>
        ) : (
          <>
            <Button onClick={() => save("draft")} disabled={saving} variant="outline" data-testid="save-draft-btn" className="h-11"><Save size={16} className="mr-1.5" /> Simpan Draft</Button>
            <Button onClick={() => save("submit")} disabled={saving} data-testid="save-submit-btn" className="bg-primary hover:bg-red-700 h-11"><Send size={16} className="mr-1.5" /> Simpan & Ajukan Persetujuan</Button>
          </>
        )}
        <Button variant="ghost" onClick={goBack} data-testid="cancel-btn" className="h-11">Batal / Kembali</Button>
      </div>
    </div>
  );
}
